/**
 * Support Mode Types
 *
 * Portable type definitions for support mode functionality.
 * These types are designed to be UI-agnostic and can be used across
 * multiple UI interfaces.
 *
 * @fileoverview TypeScript types for support mode
 */

// ============================================================================
// Core Types
// ============================================================================

/**
 * Tenant information required for support mode
 *
 * This is a minimal subset of tenant data needed for support mode.
 * When implementing support mode in a new UI, map your tenant type
 * to this interface.
 */
export interface SupportTenant {
  /** Unique tenant identifier */
  id: string;
  /** Tenant display name */
  name: string;
  /** URL-safe tenant identifier */
  slug: string;
  /** Optional manager email for display */
  managerEmail?: string | null;
  /** Whether tenant is active */
  isActive: boolean;
  /** Tenant creation date */
  createdAt: string;
}

/**
 * Support mode context state interface
 */
export interface SupportModeState {
  /** Whether admin is currently in support mode */
  isInSupportMode: boolean;
  /** The tenant being viewed in support mode */
  supportTenant: SupportTenant | null;
}

/**
 * Support mode context methods interface
 */
export interface SupportModeMethods {
  /**
   * Enter support mode for a specific tenant
   * @param tenant - The tenant to view in support mode
   */
  enterSupportMode: (tenant: SupportTenant) => void;

  /**
   * Exit support mode and return to normal admin view
   */
  exitSupportMode: () => void;
}

/**
 * Complete support mode context type combining state and methods
 */
export type SupportModeContextType = SupportModeState & SupportModeMethods;

// ============================================================================
// Constants
// ============================================================================

/**
 * LocalStorage key for persisting support mode state
 * This key is shared across all UI interfaces to maintain consistent state
 */
export const SUPPORT_MODE_STORAGE_KEY = 'xacto_support_mode';

// ============================================================================
// Helper Types
// ============================================================================

/**
 * Props for SupportModeProvider component
 */
export interface SupportModeProviderProps {
  /** Child components to wrap */
  children: React.ReactNode;
}

/**
 * Props for SupportModeBanner component
 */
export interface SupportModeBannerProps {
  /** Optional callback after exiting support mode */
  onExit?: () => void;
  /** Optional custom navigation handler (default: navigate to /admin) */
  onNavigateToAdmin?: () => void;
}
